<mat-tab-group>
  <mat-tab label="Account Configuration">
    <div class="mt-3 pl-3 pr-3">
      <form (submit)="onSave()" #f="ngForm">

        <div class="row pt-2">
          <div class="col">
            <fieldset>
              <legend><h6>Assets</h6></legend>
              <mat-form-field class="full-width mb-2" appearance="outline" subscriptSizing="dynamic" style="max-width: 300px;">
                <mat-label>Currency</mat-label>
                <mat-select
                  placeholder="Select Currency Code."
                  [(ngModel)]="config.currency_code"
                  name="currency_code"
                  id="currency_code"
                  required>
                  <mat-option *ngFor="let c of currencies" [value]="c.code">
                    <div class="currency-option">
                      <div class="currency-main">
                        <span class="currency-code">{{ c.code }}</span>&nbsp;
                        <span class="currency-symbol">{{ c.symbol }}</span>&nbsp;
                      </div>
                      <div class="currency-name">{{ c.name }}</div>
                    </div>
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <p class="field-hint">Select the currency the asset was purchased in. This affects how costs, rounding, and depreciation are calculated.</p>
              <hr>
            </fieldset>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <fieldset>
              <legend><h6>Hazards & Risks</h6></legend>
              <mat-checkbox [(ngModel)]="config.hazard_verification" matTooltip="When enabled, a manager will be automatically notified for verification whenever hazards and risks are reported by users through the mobile app." name="hazard_verification">
                Enable Manager Verification for Hazards & Risks Reports
              </mat-checkbox>
              <mat-hint>When enabled, a manager will be automatically notified for verification whenever hazards and risks are reported by users through the mobile app.</mat-hint>

              <div class="mt-3">

                <div class="mb-3">
                  <button
                    type="button"
                    mat-raised-button
                    color="secondary"
                    class="m-1 mr-2"
                    (click)="onUploadRiskMatrix()"
                    matTooltip="Upload Risk Matrix">
                    <mat-icon>assessment</mat-icon>
                    Upload Risk Matrix Definition Image
                  </button>
                </div>
                <mat-hint>
                  Upload an image that explains how your organisation defines and interprets risk ratings within the risk matrix (e.g. consequence, likelihood, and risk score definitions).
                </mat-hint>
              </div>

              <div class="mt-4">
                <div class="mb-3">
                  <button
                    type="button"
                    mat-raised-button
                    color="secondary"
                    class="m-1 mr-2"
                    (click)="onUploadHierarchyOfControl()"
                    matTooltip="Upload Hierarchy of Controls">
                    <mat-icon>vertical_align_top</mat-icon>
                    Upload Risk Controls Definition Image
                  </button>
                </div>
                <mat-hint>
                  Upload an image that defines how your organisation interprets risk controls.
                </mat-hint>
              </div>
              <hr>
            </fieldset>
          </div>
        </div>

        <div class="row">
          <div class="col mt-2">
            <fieldset>
              <legend><h6>{{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites') }}</h6></legend>
              <mat-checkbox [(ngModel)]="config.allow_auto_site_signout" matTooltip="Users will be automatically signed out from {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() }} {{ config.auto_site_signout_duration }} hours after their closing times. The duration is adjustable via the dropdown selection." name="allow_auto_site_signout">
                Enable Auto-Sign Out from {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites') }}.
              </mat-checkbox>

              <mat-form-field class="full-width my-2" appearance="outline" subscriptSizing="dynamic" style="max-width: 300px;">
                <mat-label>Duration for Auto Sign-Out from {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites') }}</mat-label>
                <mat-select
                  placeholder="Duration for Auto Sign-Out from {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites') }}"
                  [(ngModel)]="config.auto_site_signout_duration"
                  name="auto_site_signout_duration"
                  id="auto_site_signout_duration"
                  required>
                  <mat-option *ngFor="let auto_site_signout_duration_option of auto_site_signout_duration_options" [value]="auto_site_signout_duration_option">
                    {{ auto_site_signout_duration_option }} Hours
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <p class="field-hint">Users will be automatically signed out from {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() }} {{ config.auto_site_signout_duration }} hours after their closing times. The duration is adjustable via the dropdown selection.</p>

              <div class="mt-2">
                <mat-checkbox [(ngModel)]="config.enable_manual_gps_signins" matTooltip="Enable this option to allow users to manually sign in using the ‘GPS Sign-in’ button in the mobile app." name="enable_manual_gps_signins">
                  Enable Manual GPS Sign-ins
                </mat-checkbox>
                <mat-hint>Enable this option to allow users to manually sign in using the ‘GPS Sign-in’ button in the mobile app.</mat-hint>
              </div>
              <hr>
            </fieldset>
          </div>
        </div>

        <div class="row">
          <div class="col mt-2">
            <fieldset>
              <legend><h6>Notifications</h6></legend>
              <div>
                <mat-checkbox [(ngModel)]="config.induction_notifications_enabled" matTooltip="When enabled, account administrators will receive notifications as users complete their account induction process." name="induction_notifications_enabled">
                  Enable Induction Completion Notifications for Administrators
                </mat-checkbox>
                <mat-hint>When enabled, account administrators will receive notifications as users complete their account induction process.</mat-hint>
              </div>

              <div class="mt-2">
                <mat-checkbox [(ngModel)]="config.enable_site_sign_in_ack_reminders" matTooltip="Enable reminders for users to acknowledge risks 5 minutes after signing into a {{ utils.getLangTerm('parent-child-sites-combined.singular', 'Site').toLowerCase() }}, if they haven't done so already." name="enable_site_sign_in_ack_reminders">
                  Enable 5-Minute Risk Acknowledgment Reminders
                </mat-checkbox>
                <mat-hint>Enable reminders for users to acknowledge risks 5 minutes after signing into a {{ utils.getLangTerm('parent-child-sites-combined.singular', 'Site').toLowerCase() }}, if they haven't done so already.</mat-hint>
              </div>

              <div class="mt-2">
                <mat-checkbox [(ngModel)]="config.enable_site_sign_in_ack_managers" [disabled]="!config.enable_site_sign_in_ack_reminders" matTooltip="When enabled, managers will receive an alert 5 minutes after a user signs into a {{ utils.getLangTerm('parent-child-sites-combined.singular', 'Site').toLowerCase() }} but failed to acknowledge the associated risks." name="enable_site_sign_in_ack_managers">
                  Enable Manager Alerts for Unacknowledged Risks 5-Minutes After Sign-In
                </mat-checkbox>
                <mat-hint>When enabled, managers will receive an alert 5 minutes after a user signs into a {{ utils.getLangTerm('parent-child-sites-combined.singular', 'Site').toLowerCase() }} but failed to acknowledge the associated risks.</mat-hint>
              </div>

              <div class="mt-2">
                <mat-checkbox [(ngModel)]="config.include_main_contact_in_notifications" matTooltip="When enabled, the main contact of this account will be included in all notifications." name="include_main_contact_in_notifications">
                  Include the main account contact in all notifications.
                </mat-checkbox>
                <mat-hint>When enabled, the main contact of this account will be included in all notifications.</mat-hint>
              </div>
              <hr>
            </fieldset>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <fieldset>
              <legend><h6>Search Results</h6></legend>
              <mat-checkbox [(ngModel)]="config.include_in_search_results" matTooltip="Enable this option to make your account publicly visible in search results. This applies when account administrators search for contractors and when users search for their employers." name="include_in_search_results">
                Include Account in Search Results
              </mat-checkbox>
              <mat-hint>Enable this option to make your account publicly visible in search results. This applies when account administrators search for contractors and when users search for their employers.</mat-hint>
              <hr>
            </fieldset>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <fieldset>
              <legend><h6>{{ utils.getLangTerm('safety-plans.singular', 'Safety Plan') }}</h6></legend>
              <mat-form-field class="full-width mt-3 mb-4" appearance="outline" subscriptSizing="dynamic">
                <mat-label>Enter your overall {{ utils.getLangTerm('safety-plans.singular', 'Safety Plan') }} header here.</mat-label>
                <textarea
                  matInput
                  placeholder="Enter your overall {{ utils.getLangTerm('safety-plans.singular', 'Safety Plan') }} header here."
                  [(ngModel)]="config.sssp_header"
                  name="sssp_header"
                  rows="5"
                  matTooltip="Enter your overall {{ utils.getLangTerm('safety-plans.singular', 'Safety Plan') }} header here.">
                </textarea>
              </mat-form-field>
            </fieldset>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col">
            <button mat-raised-button class="my-2 ml-0 mr-2" color="primary" [disabled]="!f.valid || inProgress">
              <mat-icon>save</mat-icon>
              Save
            </button>
          </div>
        </div>

      </form>
    </div>
  </mat-tab>

  <mat-tab label="Account Data Synchronisation">
    <app-related-slave-accounts></app-related-slave-accounts>
  </mat-tab>
</mat-tab-group>
